/**
 * หนังสือรับรอง (ฝ่ายบุคคลออกให้พนักงาน) — รับรองเงินเดือน / รับรองการทำงาน.
 * เลือกพนักงาน → เลือกชนิด → (ระบุวัตถุประสงค์) → ออกเป็น PDF.
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useEmployees, useCertificatePreview, useIssueCertificate, type CertKind, type CertificatePreview } from '../../hooks';
import { EmployeePicker } from '../../components/ui/EmployeePicker';
import { useToast } from '../../components/ui/toast';
import { Award, Banknote, FileCheck2, Eye, Download } from 'lucide-react';

const KINDS: Array<{ value: CertKind; label: string; desc: string; icon: typeof Award }> = [
  { value: 'employment', label: 'หนังสือรับรองการทำงาน', desc: 'รับรองตำแหน่ง แผนก และช่วงเวลาที่ทำงาน', icon: FileCheck2 },
  { value: 'salary', label: 'หนังสือรับรองเงินเดือน', desc: 'รับรองการทำงาน + เงินเดือนปัจจุบัน (ดึงจากระบบ)', icon: Banknote },
];

const baht = (n: number) => new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n ?? 0);

export function OwnerCertificates() {
  // ออกใบรับรอง/หนังสือรับรองเงินเดือนให้คนที่ลาออกไปแล้วได้ → ต้องรวมคนออกแล้ว
  const { data: employees } = useEmployees(true, true) as {
    data: Array<{ emp_code: string; first_name: string; last_name: string; nickname?: string | null; status: string; department?: string | null; position?: string | null }> | undefined;
  };
  const previewM = useCertificatePreview();
  const issueM = useIssueCertificate();
  const toast = useToast();

  const [empCode, setEmpCode] = useState('');
  const [kind, setKind] = useState<CertKind>('employment');
  const [purpose, setPurpose] = useState('');
  const [preview, setPreview] = useState<CertificatePreview | null>(null);

  const pickable = (employees ?? []).filter((e) => e.status !== 'pending_start');

  async function handlePreview() {
    if (!empCode) { toast.error('เลือกพนักงานก่อน'); return; }
    try {
      const r = await previewM.mutateAsync({ emp_code: empCode, kind, purpose: purpose.trim() || undefined });
      setPreview(r.preview);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'ดึงข้อมูลไม่สำเร็จ'); }
  }

  async function handleIssue() {
    if (!empCode) { toast.error('เลือกพนักงานก่อน'); return; }
    try {
      const r = await issueM.mutateAsync({ emp_code: empCode, kind, purpose: purpose.trim() || undefined });
      toast.success(`ออกหนังสือรับรองสำเร็จ (เลขที่ ${r.ref_no})`);
      window.open(r.pdf_url, '_blank');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'ออกหนังสือไม่สำเร็จ'); }
  }

  return (
    <AdminLayout title="หนังสือรับรอง">
      <p className="text-sm text-gray-600 mb-3">ออกหนังสือรับรองการทำงาน / เงินเดือน ให้พนักงาน (เป็นไฟล์ PDF พิมพ์ได้)</p>

      <div className="card p-4 space-y-4 mb-4">
        {/* เลือกพนักงาน */}
        <div>
          <label className="label">พนักงาน *</label>
          <EmployeePicker
            employees={pickable}
            value={empCode || null}
            onChange={(c) => { setEmpCode(c ?? ''); setPreview(null); }}
            placeholder="— เลือกพนักงาน —"
          />
        </div>

        {/* เลือกชนิด — การ์ดใหญ่กดง่าย */}
        <div>
          <label className="label">ชนิดหนังสือรับรอง</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {KINDS.map((k) => {
              const Icon = k.icon;
              const active = kind === k.value;
              return (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => { setKind(k.value); setPreview(null); }}
                  className={`text-left rounded-xl border-2 p-3 transition ${active ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    <Icon className={`w-5 h-5 ${active ? 'text-brand-600' : 'text-gray-400'}`} />
                    {k.label}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{k.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* วัตถุประสงค์ */}
        <div>
          <label className="label">ออกให้เพื่อ (ไม่บังคับ)</label>
          <input
            value={purpose}
            onChange={(e) => { setPurpose(e.target.value); setPreview(null); }}
            placeholder="เช่น ยื่นขอสินเชื่อกับธนาคาร / ใช้ตามที่ผู้ถือประสงค์"
            className="input"
          />
        </div>

        {/* ปุ่ม — เห็นชัด: ดูตัวอย่าง (รอง) + ออกเลย (หลัก) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          <button onClick={handlePreview} disabled={previewM.isPending} className="btn-secondary w-full">
            <Eye className="w-4 h-4" /> {previewM.isPending ? 'กำลังดึง…' : 'ดูตัวอย่างข้อมูล'}
          </button>
          <button onClick={handleIssue} disabled={issueM.isPending} className="btn-cta w-full">
            <Download className="w-4 h-4" /> {issueM.isPending ? 'กำลังออก…' : 'ออกหนังสือรับรอง (PDF)'}
          </button>
        </div>
      </div>

      {/* ตัวอย่างข้อมูลก่อนออก */}
      {preview && (
        <div className="card p-4 mb-4">
          <div className="font-semibold mb-2 flex items-center gap-2">
            <Award className="w-5 h-5 text-brand-600" />
            {preview.kind === 'salary' ? 'หนังสือรับรองเงินเดือน' : 'หนังสือรับรองการทำงาน'}
          </div>
          <div className="text-xs text-gray-500 mb-3">เลขที่ {preview.refNo} · วันที่ {preview.issueDateTh}</div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              <tr><td className="py-1.5 text-gray-500">ชื่อ–นามสกุล</td><td className="py-1.5 text-right font-medium">{preview.empName}</td></tr>
              <tr><td className="py-1.5 text-gray-500">รหัสพนักงาน</td><td className="py-1.5 text-right">{preview.empCode}</td></tr>
              <tr><td className="py-1.5 text-gray-500">ตำแหน่ง</td><td className="py-1.5 text-right">{preview.position ?? '-'}</td></tr>
              <tr><td className="py-1.5 text-gray-500">แผนก</td><td className="py-1.5 text-right">{preview.department ?? '-'}</td></tr>
              <tr><td className="py-1.5 text-gray-500">วันเริ่มงาน</td><td className="py-1.5 text-right">{preview.startDateTh}</td></tr>
              <tr><td className="py-1.5 text-gray-500">สถานะ</td><td className="py-1.5 text-right">{preview.stillEmployed ? 'ยังทำงานอยู่' : `พ้นสภาพ${preview.endDateTh ? ` (${preview.endDateTh})` : ''}`}</td></tr>
              {preview.kind === 'salary' && preview.salaryAmount != null && (
                <tr className="border-t-2 font-bold">
                  <td className="py-2">เงินเดือนปัจจุบัน</td>
                  <td className="py-2 text-right text-brand-700">{baht(preview.salaryAmount)} บาท</td>
                </tr>
              )}
            </tbody>
          </table>
          {preview.kind === 'salary' && preview.salaryText && (
            <div className="text-xs text-gray-500 mt-1 text-right">({preview.salaryText})</div>
          )}
          <button onClick={handleIssue} disabled={issueM.isPending} className="btn-cta w-full mt-3">
            <Download className="w-4 h-4" /> {issueM.isPending ? 'กำลังออก…' : 'ออกหนังสือรับรอง (PDF)'}
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
